import { AppPlatform } from "app/types";
import { useNativePackages, useSubscriptionPlans } from "features/subscription";
import {
  PlanCategory,
  BillingPeriod,
} from "features/subscription/useNativePackages";

export type PriceDisplay = {
  price: number;
  priceString: string;
  currencyCode: string;
};

// DB から取れなかったときの表示用。SubscriptionPage にあったものをそのまま持つ。
const FALLBACK_PRICES: Record<
  PlanCategory,
  Record<BillingPeriod, PriceDisplay>
> = {
  standard: {
    monthly: { price: 400, priceString: "¥400", currencyCode: "JPY" },
    annual: { price: 3900, priceString: "¥3,900", currencyCode: "JPY" },
  },
  premium: {
    monthly: { price: 1500, priceString: "¥1,500", currencyCode: "JPY" },
    annual: { price: 14900, priceString: "¥14,900", currencyCode: "JPY" },
  },
};

/**
 * プランの表示価格を取得する。
 * アプリは RevenueCat のパッケージ、Web は application_subscriptions（web ストア）を参照する。
 * プラン画面とチャットの購入お願いカードで同じ値を出すために共有している。
 */
export const usePlanPrice = (enabled: boolean = true) => {
  const platform = window.parentsAppPlatform || AppPlatform.Web;
  const isNativeApp =
    platform === AppPlatform.iOS || platform === AppPlatform.Android;
  const { getPrice } = useNativePackages();
  const { data: webPlans = [] } = useSubscriptionPlans(
    "web",
    enabled && !isNativeApp,
  );

  const getPriceDisplay = (
    planCategory: PlanCategory,
    period: BillingPeriod,
  ): PriceDisplay => {
    if (isNativeApp) {
      return getPrice(planCategory, period);
    }
    const targetName =
      planCategory === "standard" ? "基本プラン" : "特別プラン";
    const foundPlan = webPlans.find(
      (p) => p.name.includes(targetName) && p.billing_period === period,
    );
    if (foundPlan) {
      const intPrice = Math.floor(foundPlan.price);
      return {
        price: intPrice,
        priceString: `¥${intPrice.toLocaleString()}`,
        currencyCode: "JPY",
      };
    }
    return FALLBACK_PRICES[planCategory][period];
  };

  return { getPriceDisplay, webPlans, isNativeApp };
};
